/**
 * Inter-frame scroll-stability gate (b17u).
 *
 * Motivation (see RC2 in the b17t live-scan diagnosis): the PiP producer
 * captures broadcast frames at its native cadence regardless of whether the
 * user is scrolling the in-game Pokedex or holding still. A frame captured
 * MID-SCROLL is sharp enough to clear the blur gate but is not a valid grid
 * snapshot: the numbers visible in the lower band belong to one row while the
 * sprite for that row has already translated into the next ROI. The per-cell
 * classifier then reads digits whose centroid lands at "center" (two rows'
 * numbers share one ROI band) and an empty top region (the sprite has scrolled
 * out), so a perfectly valid cell is classified "never seen". The post-b17t
 * device log showed 77% of cells reading numberPosition:"center" with sprite
 * "none" — the classic mid-scroll signature.
 *
 * The blur gate measures single-frame sharpness; it cannot see motion BETWEEN
 * frames. This gate fills that gap: it compares a stable horizontal band of the
 * current frame against the same band of the previous ACCEPTED frame. When the
 * band has translated more than a threshold, the frame is "scrolling" and its
 * per-cell classification is skipped (region / variant chip reads, which do not
 * depend on cell positions, still run).
 *
 * Everything here is pure and total so it can be exercised on synthetic buffers
 * in the unit tests without a camera. The data-URL wrapper
 * [[lumaBandProfileFromDataUrl]] is the only DOM-dependent entry point and
 * returns null (never gate) when it cannot decode, so a missing canvas can
 * never silently swallow the pipeline.
 */
import { loadFrameImage } from "./frameImageCache";
import { CANVAS_READBACK } from "./canvasReadback";

/** Default width of the 1D luma profile a frame band is reduced to. */
export const STABILITY_PROFILE_WIDTH = 64;

/**
 * The horizontal band, as a fraction of frame height, that the profile is
 * sampled from. 0.20..0.30 sits above the dex grid (where the region tabs /
 * header live) but on stable, high-contrast content, so vertical scroll of the
 * grid below still shifts this band's luma profile measurably while avoiding
 * the PiP overlay region and the noisy grid itself.
 */
export const STABILITY_BAND_TOP_FRAC = 0.2;
export const STABILITY_BAND_BOTTOM_FRAC = 0.3;

/**
 * Reduce a horizontal band of an RGBA frame to a fixed-width 1D luma profile.
 *
 * The band spans the full image width and the row range
 * [`bandTopFrac`, `bandBottomFrac`) of its height. It is downsampled to
 * `profileWidth` columns by averaging the luma of every source pixel that falls
 * in each output column across every row of the band. The result is a compact
 * signature of the band's horizontal luma structure that is cheap to diff
 * frame-to-frame. Pure and total.
 *
 * Returns a zero-filled profile when the band is degenerate (empty row range or
 * sub-2px width) rather than throwing, so callers never need to guard.
 */
export function lumaBandProfileFromRgba(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  profileWidth = STABILITY_PROFILE_WIDTH,
  bandTopFrac = STABILITY_BAND_TOP_FRAC,
  bandBottomFrac = STABILITY_BAND_BOTTOM_FRAC,
): number[] {
  const out = new Array<number>(profileWidth).fill(0);
  if (width < 2 || height < 1) return out;
  const y0 = Math.max(0, Math.min(height - 1, Math.floor(bandTopFrac * height)));
  const y1 = Math.max(y0 + 1, Math.min(height, Math.ceil(bandBottomFrac * height)));
  const counts = new Array<number>(profileWidth).fill(0);
  for (let y = y0; y < y1; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const l = luma(data[i], data[i + 1], data[i + 2]);
      // The Android capture paints our own HUD windows solid black before
      // the frame is encoded (ScreenCaptureService.maskHud), and the HUD's
      // default dock sits inside this band. Every open / fold / resize of
      // the card used to move the band's mean by 30-45 luma and mark the
      // frame mid-scroll (2026-09-30). Near-black pixels are not scene.
      if (l < STABILITY_MASK_LUMA) continue;
      // Map source column x to an output bucket.
      const bucket = Math.min(profileWidth - 1, Math.floor((x / width) * profileWidth));
      out[bucket] += l;
      counts[bucket] += 1;
    }
  }
  for (let b = 0; b < profileWidth; b++) {
    out[b] = counts[b] > 0 ? out[b] / counts[b] : STABILITY_MASKED_BUCKET;
  }
  return out;
}

/** Pixels darker than this (0-255 luma) are treated as the HUD mask, not scene. */
export const STABILITY_MASK_LUMA = 12;
/** A profile bucket with no scene pixels; skipped by [[frameStabilityDiff]]. */
export const STABILITY_MASKED_BUCKET = -1;

/**
 * Mean absolute difference between two luma profiles, in luma units (0..255).
 *
 * Higher = the band shifted more between the two frames = more likely mid-
 * scroll. Profiles of differing length are compared over their shared prefix
 * (defensive; in practice both come from [[lumaBandProfileFromRgba]] at the
 * same `profileWidth`). Pure and total; returns 0 when there is nothing to
 * compare.
 */
export function frameStabilityDiff(prev: number[], curr: number[]): number {
  const n = Math.min(prev.length, curr.length);
  if (n === 0) return 0;
  let total = 0;
  let compared = 0;
  for (let i = 0; i < n; i++) {
    // A bucket the HUD mask covered in either frame says nothing about
    // scroll; compare the columns both frames could see.
    if (prev[i] < 0 || curr[i] < 0) continue;
    total += Math.abs(prev[i] - curr[i]);
    compared += 1;
  }
  return compared === 0 ? 0 : total / compared;
}

export interface StabilityDecisionInput {
  /** Profile of the previous ACCEPTED frame, or null if there is none yet. */
  prevProfile: number[] | null;
  /** Profile of the current frame, or null if it could not be decoded. */
  currProfile: number[] | null;
  /** Max band diff (luma units) a frame may have and still be treated stable. */
  diffMax: number;
}

export interface StabilityDecision {
  /** True when the frame is stable enough to classify per-cell. */
  stable: boolean;
  /** The measured band diff (echoed for diagnostics); 0 when not measurable. */
  diff: number;
  /**
   * Whether the current profile should become the new "previous accepted"
   * baseline. We only advance the baseline when we have a usable current
   * profile, so an undecodable frame never poisons the comparison.
   */
  advanceBaseline: boolean;
}

/**
 * Decide whether a frame is stable enough for per-cell classification.
 *
 * Rules:
 *  - No previous baseline yet (first frame of the session): always stable, and
 *    the current profile becomes the baseline.
 *  - Current profile undecodable: treat as stable (never gate on a decode
 *    failure, mirroring the blur gate's fail-open contract) and do NOT advance
 *    the baseline.
 *  - Otherwise: stable iff the band diff is at or below `diffMax`. The current
 *    profile becomes the new baseline either way, so the NEXT frame is always
 *    compared against the most recent real frame (transitional or not) — this
 *    keeps the gate tracking continuous scroll rather than drifting against a
 *    stale dwell frame.
 *
 * Pure and total.
 */
export function decideFrameStability(input: StabilityDecisionInput): StabilityDecision {
  const { prevProfile, currProfile, diffMax } = input;
  if (currProfile == null) {
    return { stable: true, diff: 0, advanceBaseline: false };
  }
  if (prevProfile == null) {
    return { stable: true, diff: 0, advanceBaseline: true };
  }
  const diff = frameStabilityDiff(prevProfile, currProfile);
  return { stable: diff <= diffMax, diff, advanceBaseline: true };
}

/**
 * Decode a frame data URL, crop the stability band, and return its 1D luma
 * profile. Returns null (never gate) when the frame can't be decoded in this
 * environment, so a missing canvas can never silently swallow every frame.
 *
 * The frame is downsampled to at most `sampleEdge` px on its long side before
 * the band is sampled: scroll motion is a coarse, full-band shift, so the
 * profile does not need full resolution and a small decode keeps this far
 * cheaper than the OCR pass it guards.
 */
export async function lumaBandProfileFromDataUrl(
  dataUrl: string,
  sampleEdge = 160,
  profileWidth = STABILITY_PROFILE_WIDTH,
): Promise<number[] | null> {
  if (!dataUrl) return null;
  if (typeof document === "undefined") return null;
  let img: HTMLImageElement;
  try {
    img = await loadImage(dataUrl);
  } catch {
    return null;
  }
  const longest = Math.max(img.width, img.height);
  if (longest < 2) return null;
  const scale = longest > sampleEdge ? sampleEdge / longest : 1;
  const w = Math.max(2, Math.round(img.width * scale));
  const h = Math.max(2, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d", CANVAS_READBACK);
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, w, h);
  let pixels: ImageData;
  try {
    pixels = ctx.getImageData(0, 0, w, h);
  } catch {
    return null;
  }
  return lumaBandProfileFromRgba(pixels.data, w, h, profileWidth);
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  // One decode per frame, shared by every consumer (lib/frameImageCache).
  return loadFrameImage(dataUrl);
}

function luma(r: number, g: number, b: number): number {
  // Rec. 709 luma — matches frameQualityGate's pill/sharpness measure.
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
